import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { ReviewPager } from '../components/ReviewTimeline'
import { api, formatVnd, type ContentBlog, type GameServer, type ShopCard } from '../lib/api'

export function plain(html: string | null | undefined, max = 160) {
  if (!html) return ''
  const text = new DOMParser().parseFromString(html, 'text/html').body.textContent || ''
  const t = text.replace(/\s+/g, ' ').trim()
  return t.length > max ? `${t.slice(0, max).trimEnd()}…` : t
}

export function useQueryState() {
  const [params, setParams] = useSearchParams()
  const set = (patch: Record<string, string | number | null>, opts?: { replace?: boolean }) => {
    const next = new URLSearchParams(params)
    for (const [k, v] of Object.entries(patch)) {
      if (v === null || v === '' || (k === 'page' && v === 1)) next.delete(k)
      else next.set(k, String(v))
    }
    setParams(next, opts)
  }
  return [params, set] as const
}

export function SearchBox({ value, onSearch, placeholder }: { value: string; onSearch: (q: string) => void; placeholder: string }) {
  const [q, setQ] = useState(value)
  useEffect(() => setQ(value), [value])
  return (
    <form
      className="content-search"
      onSubmit={(e) => {
        e.preventDefault()
        onSearch(q.trim())
      }}
    >
      <i className="fa-solid fa-magnifying-glass" aria-hidden />
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} />
      <button className="btn btn-primary btn-sm" type="submit">
        Tìm
      </button>
    </form>
  )
}

/* ---------- FAQs / Knowledge base ---------- */

export function KnowledgeBasePage() {
  const [params, set] = useQueryState()
  const page = Number(params.get('page')) || 1
  const q = params.get('q') || ''
  const [data, setData] = useState<{ items: ContentBlog[]; total: number; total_pages: number } | null>(null)

  useEffect(() => {
    setData(null)
    api
      .knowledgeBase(page, q)
      .then((r) => setData(r))
      .catch(() => setData({ items: [], total: 0, total_pages: 1 }))
  }, [page, q])

  return (
    <div className="content-page">
      <section className="content-hero">
        <h1 className="page-title">
          <i className="fa-solid fa-circle-question" aria-hidden /> Câu hỏi thường gặp
        </h1>
        <SearchBox value={q} placeholder="Bạn cần hỗ trợ gì?" onSearch={(v) => set({ q: v, page: null })} />
      </section>
      {!data ? <p className="muted">Đang tải…</p> : null}
      {data && !data.items.length ? <p className="muted">Không tìm thấy bài phù hợp.</p> : null}
      <ol className="kb-list">
        {data?.items.map((b) => (
          <li key={b.id}>
            <Link className="kb-item" to={`/blog/${encodeURIComponent(b.slug)}`}>
              <h3>{b.title}</h3>
              <p>{plain(b.excerpt)}</p>
              {b.helpful ? (
                <span className="kb-helpful">
                  <i className="fa-solid fa-thumbs-up" aria-hidden /> {b.helpful} hữu ích
                </span>
              ) : null}
            </Link>
          </li>
        ))}
      </ol>
      {data ? (
        <ReviewPager page={page} totalPages={data.total_pages} onPage={(p) => set({ page: p })} />
      ) : null}
    </div>
  )
}

/* ---------- Resource pack / Shader ---------- */

export function ResourcePacksPage({ shader = false }: { shader?: boolean }) {
  const [params, set] = useQueryState()
  const page = Number(params.get('page')) || 1
  const type = shader ? 'shader' : ''
  const version = params.get('version') || ''
  const q = params.get('q') || ''
  const [data, setData] = useState<{
    items: ContentBlog[]
    total: number
    total_pages: number
    versions: string[]
  } | null>(null)

  useEffect(() => {
    setData((d) => (d ? { ...d, items: [] } : d))
    api
      .resourcePacks({ page, type, version, q })
      .then((r) => setData(r))
      .catch(() => setData({ items: [], total: 0, total_pages: 1, versions: [] }))
  }, [page, type, version, q])

  return (
    <div className="content-page">
      <section className="content-hero">
        <h1 className="page-title">
          <i className="fa-solid fa-box-archive" aria-hidden /> {type === 'shader' ? 'Shader Pack Minecraft' : 'Resource Pack Minecraft'}
        </h1>
        <SearchBox value={q} placeholder="Tìm pack…" onSearch={(v) => set({ q: v, page: null })} />
      </section>
      <nav className="rp-tabs" aria-label="Loại pack">
        <Link className={`rp-tab${!shader ? ' active' : ''}`} to="/tai-nguyen" aria-current={!shader ? 'page' : undefined}>
          <i className="fa-solid fa-images" aria-hidden /> Resource Pack
        </Link>
        <Link className={`rp-tab${shader ? ' active' : ''}`} to="/shader-pack" aria-current={shader ? 'page' : undefined}>
          <i className="fa-solid fa-sun" aria-hidden /> Shader Pack
        </Link>
      </nav>
      {data?.versions.length ? (
        <div className="rp-versions" aria-label="Phiên bản">
          <button
            type="button"
            className={`rp-chip${!version ? ' active' : ''}`}
            onClick={() => set({ version: null, page: null })}
          >
            Tất cả
          </button>
          {data.versions.map((v) => (
            <button
              key={v}
              type="button"
              className={`rp-chip${version === v ? ' active' : ''}`}
              onClick={() => set({ version: v, page: null })}
            >
              {v}
            </button>
          ))}
        </div>
      ) : null}
      {data ? <p className="muted rp-count">{data.total.toLocaleString('vi-VN')} pack</p> : <p className="muted">Đang tải…</p>}
      <div className="blog-grid">
        {data?.items.map((b) => (
          <Link key={b.id} className="blog-card card-link" to={`/blog/${encodeURIComponent(b.slug)}`}>
            <div className="blog-card-media">
              {b.image_url ? <img src={b.image_url} alt="" loading="lazy" /> : <i className="fa-solid fa-cube rp-noimg" aria-hidden />}
            </div>
            <div className="blog-card-body">
              <h3>{b.title}</h3>
              {b.versions.length ? (
                <p className="rp-ver">
                  {b.versions.length > 4
                    ? `${b.versions[0]} – ${b.versions[b.versions.length - 1]}`
                    : b.versions.join(' · ')}
                </p>
              ) : null}
            </div>
          </Link>
        ))}
      </div>
      {data ? <ReviewPager page={page} totalPages={data.total_pages} onPage={(p) => set({ page: p })} /> : null}
    </div>
  )
}

/* ---------- Server list ---------- */

function CopyIp({ ip }: { ip: string }) {
  const [done, setDone] = useState(false)
  return (
    <button
      type="button"
      className="srv-ip"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(ip)
          setDone(true)
          window.setTimeout(() => setDone(false), 1500)
        } catch {
          /* ignore */
        }
      }}
    >
      <code>{ip}</code>
      <i className={`fa-solid ${done ? 'fa-check' : 'fa-copy'}`} aria-hidden />
    </button>
  )
}

function AddServerModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({ name: '', ip: '', web: '', description: '', modes: '', type: '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState('')
  const field = (k: keyof typeof form) => ({
    value: form[k],
    onChange: (e: { target: { value: string } }) => setForm((f) => ({ ...f, [k]: e.target.value })),
  })

  async function submit(e: FormEvent) {
    e.preventDefault()
    setErr('')
    setBusy(true)
    try {
      const r = await api.addServer({
        ...form,
        web: form.web || undefined,
        description: form.description || undefined,
      })
      setDone(r.message)
    } catch (ex) {
      setErr((ex as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="amc-modal-backdrop" onClick={onClose}>
      <div className="amc-modal" role="dialog" aria-modal="true" aria-label="Thêm server" onClick={(e) => e.stopPropagation()}>
        <h2>Thêm server</h2>
        {done ? (
          <>
            <p>{done}</p>
            <div className="amc-modal-actions">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                Đóng
              </button>
            </div>
          </>
        ) : (
          <form className="form-stack" onSubmit={submit}>
            <label>
              Tên server
              <input {...field('name')} required maxLength={120} autoFocus />
            </label>
            <label>
              IP server
              <input {...field('ip')} required maxLength={120} placeholder="play.example.vn" />
            </label>
            <label>
              Chế độ chơi
              <input {...field('modes')} required maxLength={255} placeholder="Survival, Skyblock…" />
            </label>
            <label>
              Loại server
              <select {...field('type')} required>
                <option value="">Chọn loại</option>
                <option value="free">Free (không cần bản quyền)</option>
                <option value="premium">Premium (cần bản quyền)</option>
              </select>
            </label>
            <label>
              Website
              <input {...field('web')} maxLength={255} placeholder="Tuỳ chọn" />
            </label>
            <label>
              Mô tả
              <textarea {...field('description')} rows={3} maxLength={1000} />
            </label>
            {err ? <p className="note">{err}</p> : null}
            <div className="amc-modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Hủy
              </button>
              <button type="submit" className="btn btn-primary" disabled={busy}>
                {busy ? 'Đang gửi…' : 'Thêm server'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export function ServerListPage() {
  const { user } = useAuth()
  const nav = useNavigate()
  const [params, set] = useQueryState()
  const page = Number(params.get('page')) || 1
  const q = params.get('q') || ''
  const type = params.get('type') || ''
  const [data, setData] = useState<{ items: GameServer[]; total: number; total_pages: number } | null>(null)
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    setData(null)
    api
      .servers({ page, q, type })
      .then((r) => setData(r))
      .catch(() => setData({ items: [], total: 0, total_pages: 1 }))
  }, [page, q, type])

  return (
    <div className="content-page">
      <section className="content-hero">
        <h1 className="page-title">
          <i className="fa-solid fa-server" aria-hidden /> Máy chủ Minecraft Việt Nam
        </h1>
        <SearchBox value={q} placeholder="Tên hoặc IP server…" onSearch={(v) => set({ q: v, page: null })} />
      </section>
      <div className="srv-toolbar">
        <div className="rp-versions">
          {[
            ['', 'Tất cả'],
            ['free', 'Free'],
            ['premium', 'Premium'],
          ].map(([v, label]) => (
            <button
              key={v}
              type="button"
              className={`rp-chip${type === v ? ' active' : ''}`}
              onClick={() => set({ type: v || null, page: null })}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn btn-primary btn-sm"
          onClick={() => (user ? setAdding(true) : nav('/login', { state: { from: '/may-chu-minecraft' } }))}
        >
          <i className="fa-solid fa-plus" aria-hidden /> Thêm server
        </button>
      </div>
      {!data ? <p className="muted">Đang tải…</p> : null}
      {data && !data.items.length ? <p className="muted">Không có server phù hợp.</p> : null}
      <ol className="srv-list">
        {data?.items.map((s, i) => (
          <li key={s.id} className="srv-item">
            <span className="srv-rank">#{(page - 1) * 25 + i + 1}</span>
            {s.icon ? <img className="srv-icon" src={s.icon} alt="" loading="lazy" /> : <i className="fa-solid fa-cube srv-icon" aria-hidden />}
            <div className="srv-body">
              <h3>
                {s.name}
                <span className={`acc-badge ${s.type === 'premium' ? 'acc-badge-warn' : 'acc-badge-ok'}`}>
                  {s.type === 'premium' ? 'Premium' : 'Free'}
                </span>
              </h3>
              <CopyIp ip={s.ip} />
              {s.modes ? <p className="muted srv-modes">{s.modes.replace(/,\s*$/, '')}</p> : null}
            </div>
            <div className="srv-side">
              <span className="srv-players">
                <i className="fa-solid fa-user" aria-hidden /> {s.players.toLocaleString('vi-VN')}
                {s.max_players ? ` / ${s.max_players.toLocaleString('vi-VN')}` : ''}
              </span>
              {s.version ? <span className="muted">{s.version}</span> : null}
              {s.web ? (
                <a href={/^https?:\/\//.test(s.web) ? s.web : `https://${s.web}`} target="_blank" rel="noreferrer nofollow">
                  <i className="fa-solid fa-globe" aria-hidden /> Website
                </a>
              ) : null}
            </div>
          </li>
        ))}
      </ol>
      {data ? <ReviewPager page={page} totalPages={data.total_pages} onPage={(p) => set({ page: p })} /> : null}
      {adding ? <AddServerModal onClose={() => setAdding(false)} /> : null}
    </div>
  )
}

/* ---------- Tải game ---------- */

type DlGroup = { title: string; links: [string, string][] }
type DlSection = { title: string; lead?: string; groups: DlGroup[] }

const DOWNLOADS: DlSection[] = [
  {
    title: 'Java cho Minecraft PC',
    lead: 'Cần cài Java (JRE 1.8) trên máy tính để chơi Minecraft.',
    groups: [
      { title: 'Java', links: [['Java Runtime Environment 1.8.0', 'https://www.java.com/en/download/manual.jsp']] },
    ],
  },
  {
    title: 'Minecraft PC miễn phí — không cần tài khoản',
    groups: [
      {
        title: 'Tlauncher',
        links: [
          ['Windows', 'https://drive.google.com/file/d/1y9DyLVD5s9CylRdCziWDpXDjifz91mGi/view?usp=drive_link'],
          ['Linux / macOS (JAR)', 'https://drive.google.com/file/d/1culBmyy2molMySzsAxQDqJ57tiQifTtQ/view?usp=drive_link'],
        ],
      },
      {
        title: 'TL Legacy Launcher',
        links: [
          ['Windows', 'https://drive.google.com/file/d/110t-N4UPrsKmwrE2GBwv9X7bTxF15m8N/view?usp=drive_link'],
          ['macOS', 'https://drive.google.com/file/d/1mGqrffaTRpa4rYiPq6nt3STHUW20H2a-/view?usp=drive_link'],
          ['Ubuntu Linux', 'https://drive.google.com/file/d/18NTeXekEEw1yXpgwGeNk780qBnfHXo3H/view?usp=drive_link'],
          ['File JAR', 'https://drive.google.com/file/d/1QBxxLGOb6pyZo_l7WVvNahYZgrz0VEIX/view?usp=drive_link'],
        ],
      },
      {
        title: 'Fenix Launcher',
        links: [
          ['Windows (EXE)', 'https://drive.google.com/file/d/19VHU3zcmc_Esm8ntiw-lwNq8B3iljdS-/view?usp=drive_link'],
          ['File JAR', 'https://drive.google.com/file/d/15q76Smyj30tPUbFCqh-FkrFtTazMGFk8/view?usp=drive_link'],
        ],
      },
      {
        title: 'Shignima Launcher',
        links: [
          ['File JAR (Windows / Linux / macOS)', 'https://drive.google.com/file/d/1X8swyoNA0oODoXJ-Vypfxuv_eTQrGYgT/view?usp=drive_link'],
          ['Windows (EXE)', 'https://drive.google.com/file/d/1FymMvpjYsJBX3w8yB6F2K_962O8sy8Qm/view?usp=drive_link'],
        ],
      },
      {
        title: 'Titan Launcher',
        links: [
          ['All Version (JAR)', 'https://drive.google.com/file/d/1lISiuCaUZ7_pGTJbyloKX-flrovCIJwo/view?usp=drive_link'],
          ['Windows (EXE)', 'https://drive.google.com/file/d/10IhYEi2rTxDYza0Zc7lAc_RLa0FaKuUO/view?usp=drive_link'],
        ],
      },
    ],
  },
  {
    title: 'Minecraft PC — cần tài khoản bản quyền',
    lead: 'Minecraft Launcher chính thức có trong Microsoft Store (Windows 10 / 11) — cần mua game.',
    groups: [
      {
        title: 'Lunar Client (PvP, Skywars, Bedwars)',
        links: [
          ['Windows', 'https://launcherupdates.lunarclientcdn.com/Lunar%20Client%20v2.15.1.exe'],
          ['macOS', 'https://launcherupdates.lunarclientcdn.com/Lunar%20Client%20v2.15.1.dmg'],
          ['Linux (AppImage)', 'https://launcherupdates.lunarclientcdn.com/Lunar%20Client-2.15.1.AppImage'],
        ],
      },
      {
        title: 'Badlion Client',
        links: [
          ['Windows', 'https://client-updates-cdn77.badlion.net/Badlion%20Client%20Setup%203.16.0.exe'],
          ['macOS', 'https://client-updates-cdn77.badlion.net/Badlion%20Client-3.15.1.dmg'],
          ['Linux', 'https://client-updates-cdn77.badlion.net/BadlionClient'],
        ],
      },
      {
        title: 'LabyMod Launcher',
        links: [
          ['Windows', 'https://releases.r2.labymod.net/launcher/win32/x64/LabyModLauncherSetup-latest.exe'],
          ['macOS Intel', 'https://releases.r2.labymod.net/launcher/darwin/x64/LabyMod%20Launcher-latest-x64.dmg'],
          ['macOS ARM', 'https://releases.r2.labymod.net/launcher/darwin/arm64/LabyMod%20Launcher-latest-arm64.dmg'],
          ['Linux (RPM)', 'https://releases.r2.labymod.net/launcher/linux/x64/labymodlauncher-latest-1.x86_64.rpm'],
          ['Linux (DEB)', 'https://releases.r2.labymod.net/launcher/linux/x64/labymodlauncher_latest_amd64.deb'],
          ['Linux (AppImage)', 'https://releases.r2.labymod.net/launcher/linux/x64/LabyMod%20Launcher-latest.AppImage'],
        ],
      },
      {
        title: 'Salwyrr Client',
        links: [
          ['File JAR (macOS / Windows / Linux)', 'https://drive.google.com/file/d/1Q0ji8qLoU5Irsi8n-OGgD-SKsssmlOh4/view?usp=drive_link'],
          ['Windows (EXE)', 'https://drive.google.com/file/d/13AAmjthw-3FrzZcVpe1hbqvCAF0WcJvA/view?usp=drive_link'],
        ],
      },
      {
        title: 'Feather Client (Forge & Fabric)',
        links: [['Feather Client Launcher', 'https://drive.google.com/file/d/1xhYHpU8UXeQGipRH6hpISuWsAQXOn1S1/view?usp=drive_link']],
      },
    ],
  },
]

export function DownloadPage() {
  const [products, setProducts] = useState<ShopCard[]>([])
  useEffect(() => {
    api
      .downloadProducts()
      .then((r) => setProducts(r.products))
      .catch(() => setProducts([]))
  }, [])

  return (
    <div className="content-page">
      <section className="content-hero">
        <h1 className="page-title">
          <i className="fa-solid fa-download" aria-hidden /> Tải Minecraft miễn phí
        </h1>
      </section>
      <div className="dl-layout">
        <div className="dl-main">
          {DOWNLOADS.map((s) => (
            <section key={s.title} className="panel dl-section">
              <h2>{s.title}</h2>
              {s.lead ? <p className="muted">{s.lead}</p> : null}
              {s.groups.map((g) => (
                <div key={g.title} className="dl-group">
                  <h3>{g.title}</h3>
                  <div className="dl-links">
                    {g.links.map(([label, href]) => (
                      <a key={href} className="dl-link" href={href} target="_blank" rel="noreferrer nofollow">
                        <i className="fa-solid fa-download" aria-hidden /> {label}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </section>
          ))}
          <p className="muted">Link lỗi? Nhắn Zalo để nhận link mới.</p>
        </div>
        {products.length ? (
          <aside className="dl-side">
            <h2>Mua bản quyền</h2>
            {products.map((p) => (
              <Link key={p.id} className="dl-product" to={p.id === 14 ? '/mua-minecraft' : `/san-pham/${encodeURIComponent(p.slug)}`}>
                {p.image_url ? <img src={p.image_url} alt="" loading="lazy" /> : null}
                <strong>{p.name}</strong>
                <span className="dl-price">
                  {formatVnd(p.price_transfer)} <s className="muted">{formatVnd(p.price)}</s>
                </span>
              </Link>
            ))}
          </aside>
        ) : null}
      </div>
    </div>
  )
}
